import { api } from './api';

// myRecords — the viewer's OWN catalogue, for `my_record` placements.
//
// Every other placement type draws from house fill: a shared pool of somebody
// else's content, picked to keep inventory from looking empty. A record plaque
// is the opposite — it can only ever show YOUR track, so it needs its own tiny
// cache with the same shape as houseFill (load once, notify, index by slot).
//
// Slots are assigned by placement_id order within a room, so the wall is stable:
// the plaque you released a record on yesterday is the same plaque today, and a
// new track fills the next empty frame rather than shuffling the whole wall.
let tracks = null;
let loading = null;
let forAvatar = null;
const subs = new Set();

// THE VISIT BASELINE — which takes already existed when you walked in.
//
// "Invisible until needed" can't mean "hide when empty", because a catalogue
// with old unreleased takes in it is never empty, and the board would be
// permanently stacked with work you already decided not to decide. Needed means
// NEW: a take that arrived while you were standing here.
//
// Captured as a SET OF IDS rather than a timestamp on purpose — the client's
// clock and the server's created_at are not the same clock, and a few seconds
// of skew either way is the difference between the reveal working and the board
// silently staying empty.
let baseline = null;   // Set<trackId> | null (null = not established yet)

export function markVisitBaseline() {
  baseline = new Set((tracks || []).map((t) => t.track_id || t.id));
}
export function clearVisitBaseline() { baseline = null; }
const isNew = (t) => !!baseline && !baseline.has(t.track_id || t.id);

function notify() { subs.forEach((fn) => { try { fn(); } catch { /* subscriber's problem */ } }); }

export function ensureMyRecords(avatarId) {
  if (!avatarId) return Promise.resolve();
  if (forAvatar === avatarId && (tracks || loading)) return loading || Promise.resolve();
  forAvatar = avatarId;
  tracks = null;
  loading = api.avatarTracks(avatarId)
    .then((d) => { tracks = d?.tracks || []; })
    .catch(() => { tracks = []; })
    .finally(() => { loading = null; notify(); });
  return loading;
}

export function onMyRecords(fn) { subs.add(fn); return () => subs.delete(fn); }

// Re-read after a release or a new artwork, so the wall reflects it without a
// reload. Callers await this before closing their panel.
export function refreshMyRecords() {
  const id = forAvatar;
  forAvatar = null;
  return ensureMyRecords(id);
}

const isOut = (t) => t.status === 'released' || t.is_released;

/**
 * The track in a given frame.
 *
 * TWO SOURCES, because the studio's two surfaces are two ends of one pipeline:
 *
 *   'undecided' — the RESULT CARDS at the board. Takes that came back THIS
 *                 VISIT and have not been put out. The board is empty when you
 *                 walk in and fills when the room hands you something.
 *   'released'  — THE WALL behind you. Records you actually put out, so "each
 *                 release fills a frame" is literally true.
 *
 * Releasing MOVES a record from one to the other, which is why neither list is
 * "everything": a track is either still your problem or it is on the wall. It
 * also means nothing is ever unreachable — an old take you never released is
 * still sitting on a card waiting for you.
 */
export function myRecordFor(source, slotIndex) {
  if (!tracks) return null;
  const list = source === 'released'
    ? tracks.filter(isOut)
    // Fresh AND undecided: a take that came back this visit and hasn't been put
    // out. Releasing it moves it to the wall and clears the card.
    : tracks.filter((t) => !isOut(t) && isNew(t));
  return list[slotIndex] || null;
}

/** How many takes are still waiting on a decision. */
export function undecidedCount() {
  return tracks ? tracks.filter((t) => !isOut(t)).length : 0;
}

/** Every take waiting on a decision — the phone shows all of them, not just
 *  this visit's, because the phone is where you catch up on what you missed. */
export function undecidedRecords() {
  return tracks ? tracks.filter((t) => !isOut(t)) : [];
}

/** Still cooking. Drives the phone's poll — no job, no polling. */
export function generatingRecords() {
  return tracks ? tracks.filter((t) => t.status === 'generating') : [];
}

export function myRecordsLoaded() { return tracks !== null; }

// How many frames are actually FILLED. A plaque is earned by releasing, not by
// generating — an unreleased track sits in the catalogue, not on the wall — so
// this is the number the wall and the `release_track` objective both count.
export function releasedCount() {
  if (!tracks) return 0;
  return tracks.filter(isOut).length;
}
export function myRecordCount() { return tracks ? tracks.length : 0; }
